import { clamp } from "lodash";
import { DateTime, Duration } from "luxon";

import type { Item } from "@/store/types";

/**
 * Pure rules for the new-message ding — which items ding, and which sound and volume settings read as valid — kept
 * free of window/audio imports so they can be unit-tested directly. The playback side lives in `newMessageSound.ts`.
 */

/**
 * Items older than this are catch-up inserts — e.g. an AppSync update to an old message the local DB
 * never loaded — not live arrivals, so they never ding.
 */
export const MAX_AUDIBLE_ITEM_AGE_MS = Duration.fromObject({ minutes: 5 }).toMillis();

/** How widely the ding applies, mirroring the `newMessageSoundScope` web setting. */
export type NewMessageSoundScope = "off" | "myDrivers" | "allDrivers";

/** Which sound the ding plays, mirroring the `newMessageSoundId` web setting. */
export type NewMessageSoundId = "beep" | "pedalSteel" | "drum";

/** Every sound id, in the order the profile page lists them. */
export const NEW_MESSAGE_SOUND_IDS: readonly NewMessageSoundId[] = ["beep", "pedalSteel", "drum"];

/** The sound a member who never chose one hears: the original ding. */
export const DEFAULT_NEW_MESSAGE_SOUND_ID: NewMessageSoundId = "beep";

/**
 * The quietest volume, in percent. Above zero so the scope's Off stays the only setting that silences the ding.
 */
export const MIN_NEW_MESSAGE_SOUND_VOLUME = 10;

/** The loudest volume, in percent: the ding's original level, and the default. */
export const MAX_NEW_MESSAGE_SOUND_VOLUME = 100;

/** The volume slider's step, in percent. */
export const NEW_MESSAGE_SOUND_VOLUME_STEP = 10;

/**
 * The sound id `raw` names, or undefined when it names none, so an unreadable value falls through to the next source.
 */
export function readNewMessageSoundId(raw: unknown): NewMessageSoundId | undefined {
  return NEW_MESSAGE_SOUND_IDS.find((id) => id === raw);
}

/**
 * The volume percent `raw` holds, rounded to the step and clamped to the allowed range, or undefined when it holds no
 * finite number, so an unreadable value falls through to the next source.
 */
export function readNewMessageSoundVolume(raw: unknown): number | undefined {
  if (typeof raw !== "number" || !Number.isFinite(raw)) return undefined;
  const stepped = Math.round(raw / NEW_MESSAGE_SOUND_VOLUME_STEP) * NEW_MESSAGE_SOUND_VOLUME_STEP;
  return clamp(stepped, MIN_NEW_MESSAGE_SOUND_VOLUME, MAX_NEW_MESSAGE_SOUND_VOLUME);
}

/** The viewer-specific context the predicate needs beyond the item itself. */
export interface AudibleItemContext {
  myAccountId: string;
  scope: NewMessageSoundScope;
  /**
   * True when the item's feed belongs to one of my drivers. Injected so this module stays free of
   * store imports and remains directly unit-testable; consulted only when `scope === "myDrivers"`.
   */
  isMyDriverFeed: (feedId: string) => boolean;
  /**
   * True when the local permission records hold an enabled `read` permission on the feed for the
   * current workspace membership. AppSync keeps those records live — the server publishes disabled
   * rows on revocation — so grants and revocations apply mid-session without a reload. Injected on
   * the same terms as `isMyDriverFeed`; consulted only when `scope === "allDrivers"`.
   */
  hasReadPermission: (feedId: string) => boolean;
}

/**
 * True when an upserted item warrants a ding: brand-new to the local DB (`firstInsert` is cleared on
 * updates and on rows already loaded by bootstrap, so hydration never dings), recent, live, audible,
 * and authored by the feed's alias member (a driver) — mirroring the alias-feed unread rule in
 * `calculateUnreads.tsx` that feeds the tab badge, so other dispatchers' messages never ding. On top
 * of that the item must fall inside `ctx.scope`: nothing dings when it is off, only feeds on my My
 * Drivers list ding under `myDrivers`, and under `allDrivers` only feeds I currently hold read
 * permission on.
 */
export function isAudibleInboundItem(item: Item, ctx: AudibleItemContext, now = Date.now()): boolean {
  if (ctx.scope === "off") return false;
  // The scope lookup goes last: it reaches into the store, so it only runs for an item the cheap
  // field checks have already cleared.
  return Boolean(
    item.firstInsert &&
      !item.deletedAt &&
      !item.isSilent &&
      !item.isFromBot &&
      item.isFromAliasMember &&
      item.accountId &&
      item.accountId !== ctx.myAccountId &&
      item.createdAt &&
      DateTime.fromMillis(now)
        .diff(DateTime.fromJSDate(new Date(item.createdAt)))
        .toMillis() < MAX_AUDIBLE_ITEM_AGE_MS &&
      item.feedId &&
      (ctx.scope === "allDrivers" ? ctx.hasReadPermission(item.feedId) : ctx.isMyDriverFeed(item.feedId)),
  );
}
